import { useState, useCallback } from 'react';
import styles from './license-plate-controller-card.module.sass';
import TextBlurButton from 'componets/text-blur-button/text-blur-button';
import LightBox from 'componets/light-box';
import { LicensePlateControllerListItem } from './license-plate-controller-list-item';
import { energySources, vehicleTypes, oilTypes } from 'config/vehicle';
import { useLicensePlateDelete } from '../use-license-plate-delete';
import analytics from 'service/analytics';
import { useQueryClient } from '@tanstack/react-query';
import { EditLicensePlateInfoModal } from '../edit-license-plate-info-modal';
import { EditTaxSettingModal } from '../edit-tax-setting-modal';

export const LicensePlateControllerCard = ({
  onClose,
  oilType,
  vehicleType,
  energySource,
  taxSetting,
  licensePlateId,
  plateNumber,
}) => {
  const queryClient = useQueryClient();
  const enterpriseTaxSettings = queryClient.getQueryData(['tax-settings'])?.data?.data ?? [];

  const [isDeleteModalShow, setIsDeleteModalShow] = useState(false);
  const [isUpdateModalShow, setIsUpdateModalShow] = useState(false);
  const [isUpdateTaxSettingModalShow, setIsUpdateTaxSettingModalShow] = useState(false);

  const [isLicensePlateDeleteLoading, setIsLicensePlateDeleteLoading] = useState(false);

  const { deleteLicensePlateMutateAsync } = useLicensePlateDelete();

  const plateInfo = getPlateInfo({ energySource, vehicleType, oilType });

  const taxSettingInfo = `${taxSetting?.taxIdNumber} (${taxSetting?.title})`;

  const handlePlateDelete = useCallback(async () => {
    if (isLicensePlateDeleteLoading) return;

    analytics.licensePlatesRemovePlateConfirmClick();

    setIsLicensePlateDeleteLoading(true);

    try {
      await deleteLicensePlateMutateAsync(licensePlateId);
      await queryClient.invalidateQueries('license-plates');
      setIsDeleteModalShow(false);
      onClose();
    } catch (err) {
      console.log(err);
    } finally {
      setIsLicensePlateDeleteLoading(false);
    }
  }, [licensePlateId, isLicensePlateDeleteLoading, queryClient, onClose, deleteLicensePlateMutateAsync]);

  return (
    <>
      <EditLicensePlateInfoModal
        isOpen={isUpdateModalShow}
        licensePlateId={licensePlateId}
        plateNumber={plateNumber}
        defaultEnergySource={energySource}
        defaultVehicleType={vehicleType}
        defaultOilType={oilType}
        onClose={() => setIsUpdateModalShow(false)}
      />
      <EditTaxSettingModal
        isOpen={isUpdateTaxSettingModalShow}
        onClose={() => setIsUpdateTaxSettingModalShow(false)}
        licensePlateId={licensePlateId}
        plateNumber={plateNumber}
        taxSetting={taxSetting}
        enterpriseTaxSettings={enterpriseTaxSettings}
      />
      <LightBox
        show={isDeleteModalShow}
        title={`確認移除車牌 ${plateNumber}？`}
        content="此車牌將在 2 日內完成移除，成功移除前若前往加油依然會被扣款。"
        onCancel={() => {
          analytics.licensePlatesRemovePlateCancelClick();
          setIsDeleteModalShow(false);
        }}
        onApprove={handlePlateDelete}
        loading={isLicensePlateDeleteLoading}
      />
      <div className={styles['card']}>
        <div className={styles['title-section']}>
          <div className={styles['title']}>修改車牌 {plateNumber}</div>
          <TextBlurButton onClick={onClose}>取消</TextBlurButton>
        </div>
        <ul className={styles['list']}>
          <LicensePlateControllerListItem
            title="車牌資料"
            content={plateInfo}
            onClick={() => {
              analytics.licensePlatesEditPlateInfoButtonClick({ plateNumber });
              setIsUpdateModalShow(true);
            }}
          />
          <LicensePlateControllerListItem
            title="發票歸帳"
            content={taxSettingInfo}
            onClick={() => {
              analytics.licensePlatesEditTaxSettingButtonClick({ plateNumber });
              setIsUpdateTaxSettingModalShow(true);
            }}
          />
          <li className={styles['list-item']} onClick={() => setIsDeleteModalShow(true)}>
            <div className={styles['list-item-alert']}>移除車牌</div>
          </li>
        </ul>
      </div>
    </>
  );
};

function getPlateInfo({ energySource, vehicleType, oilType }) {
  const energySourceLabel = getEnergySourceLabel(energySource);
  const vehicleTypeLabel = getVehicleTypeLabel(vehicleType);
  const oilTypeLabel = getOilTypeLabel(oilType);

  let plateInfo = '';

  if (energySourceLabel) plateInfo += energySourceLabel;

  if (vehicleTypeLabel) {
    plateInfo += plateInfo ? `・${vehicleTypeLabel}` : vehicleTypeLabel;
  }

  if (energySource !== 'electric' && oilTypeLabel) {
    plateInfo += plateInfo ? `・${oilTypeLabel}` : oilTypeLabel;
  }

  return plateInfo;
}

function getEnergySourceLabel(energySource) {
  return energySources.find(({ id }) => id === energySource)?.value;
}

function getVehicleTypeLabel(vehicleType) {
  return vehicleTypes.find(({ id }) => id === vehicleType)?.value;
}

function getOilTypeLabel(oilType) {
  return oilTypes.find(({ id }) => id === oilType)?.value;
}
